/**
 * 開発者設定 API サービス
 * 現在はモックサービスを使用、将来的にAWS APIに切り替え予定
 *
 * このファイルは抽象化層として機能し、モックサービスとAWS APIの
 * 切り替えを簡単に行えるようにします。
 */

import mockService from './developerSettingsMockService.js'

// 環境設定（将来的には環境変数で制御）
const API_CONFIG = {
  // モックサービスを使用するかどうか
  USE_MOCK: true, // process.env.VUE_APP_USE_MOCK === 'true'

  // AWS API の設定（将来的に使用）
  AWS_API_NAME: 'cleanAPI',
  AWS_API_BASE_PATH: '/api/v1'
}

/**
 * AWS API サービス（将来の実装予定）
 * 現在は未実装のプレースホルダー
 */
const awsApiService = {
  async getFacilities() {
    // 将来の実装例:
    // import { API } from 'aws-amplify'
    // const response = await API.get(API_CONFIG.AWS_API_NAME, `${API_CONFIG.AWS_API_BASE_PATH}/facilities`)
    // return response.map(facility => ({
    //   id: facility.facility_id,
    //   name: facility.facility_name,
    //   latitude: facility.latitude,
    //   longitude: facility.longitude,
    //   updatedAt: facility.updated_at
    // }))

    throw new Error('AWS API は未実装です。モックサービスを使用してください。')
  },

  async getCoordinateCorrection() {
    // 将来の実装例:
    // const response = await API.get(API_CONFIG.AWS_API_NAME, `${API_CONFIG.AWS_API_BASE_PATH}/coordinate-correction`)
    // return {
    //   latitudeOffset: response.latitude_offset,
    //   longitudeOffset: response.longitude_offset,
    //   updatedAt: response.updated_at
    // }

    throw new Error('AWS API は未実装です。モックサービスを使用してください。')
  },

  async updateFacilityLocation(facilityId, latitude, longitude) {
    // 将来の実装例:
    // await API.put(API_CONFIG.AWS_API_NAME, `${API_CONFIG.AWS_API_BASE_PATH}/facilities/${facilityId}`, {
    //   body: {
    //     latitude: latitude,
    //     longitude: longitude
    //   }
    // })

    throw new Error('AWS API は未実装です。モックサービスを使用してください。')
  },

  async clearFacilityLocation(facilityId) {
    // 将来の実装例:
    // await API.put(API_CONFIG.AWS_API_NAME, `${API_CONFIG.AWS_API_BASE_PATH}/facilities/${facilityId}`, {
    //   body: {
    //     latitude: null,
    //     longitude: null
    //   }
    // })

    throw new Error('AWS API は未実装です。モックサービスを使用してください。')
  },

  async updateCoordinateCorrection(latitudeOffset, longitudeOffset) {
    // 将来の実装例:
    // await API.put(API_CONFIG.AWS_API_NAME, `${API_CONFIG.AWS_API_BASE_PATH}/coordinate-correction`, {
    //   body: {
    //     latitude_offset: latitudeOffset,
    //     longitude_offset: longitudeOffset
    //   }
    // })

    throw new Error('AWS API は未実装です。モックサービスを使用してください。')
  }
}

/**
 * 使用するサービスを決定
 * API_CONFIG.USE_MOCK の値に基づいてモックサービスまたはAWS APIを選択
 */
const getApiService = () => {
  if (API_CONFIG.USE_MOCK) {
    console.log('[API Service] モックサービスを使用します')
    return mockService
  } else {
    console.log('[API Service] AWS APIを使用します')
    return awsApiService
  }
}

/**
 * 統一された API サービスインターフェース
 * フロントエンドから使用される実際のAPIサービス
 */
const developerSettingsApi = {
  /**
   * 施設情報を取得
   * @returns {Promise<Array>} 施設情報の配列
   */
  async getFacilities() {
    const service = getApiService()
    return await service.getFacilities()
  },

  /**
   * 座標変換補正値を取得
   * @returns {Promise<Object>} 座標変換補正値
   */
  async getCoordinateCorrection() {
    const service = getApiService()
    return await service.getCoordinateCorrection()
  },

  /**
   * 施設の位置情報を更新
   * @param {number} facilityId - 施設ID
   * @param {number|null} latitude - 緯度
   * @param {number|null} longitude - 経度
   * @returns {Promise<Object>} 更新された施設情報
   */
  async updateFacilityLocation(facilityId, latitude, longitude) {
    const service = getApiService()
    return await service.updateFacilityLocation(facilityId, latitude, longitude)
  },

  /**
   * 施設の位置情報をクリア
   * @param {number} facilityId - 施設ID
   * @returns {Promise<Object>} 更新された施設情報
   */
  async clearFacilityLocation(facilityId) {
    const service = getApiService()
    return await service.clearFacilityLocation(facilityId)
  },

  /**
   * 座標変換補正値を更新
   * @param {number} latitudeOffset - 緯度補正値
   * @param {number} longitudeOffset - 経度補正値
   * @returns {Promise<Object>} 更新された座標変換補正値
   */
  async updateCoordinateCorrection(latitudeOffset, longitudeOffset) {
    const service = getApiService()
    return await service.updateCoordinateCorrection(latitudeOffset, longitudeOffset)
  },

  // ===========================
  // アカウント関連のメソッド
  // ===========================

  /**
   * アカウント一覧を取得
   * @returns {Promise<Array>} アカウント一覧
   */
  async getAccounts() {
    const service = getApiService()
    return await service.getAccounts()
  },

  /**
   * 新規アカウントを作成
   * @param {Object} accountData - 作成するアカウントデータ
   * @returns {Promise<Object>} 作成されたアカウント
   */
  async createAccount(accountData) {
    const service = getApiService()
    return await service.createAccount(accountData)
  },

  /**
   * アカウント情報を更新
   * @param {number} accountId - 更新するアカウントのID
   * @param {Object} accountData - 更新データ
   * @returns {Promise<Object>} 更新されたアカウント
   */
  async updateAccount(accountId, accountData) {
    const service = getApiService()
    return await service.updateAccount(accountId, accountData)
  },

  /**
   * アカウントを削除
   * @param {number} accountId - アカウントID
   * @returns {Promise<void>}
   */
  async deleteAccount(accountId) {
    const service = getApiService()
    return await service.deleteAccount(accountId)
  },

  /**
   * 属性に応じた企業リストを取得
   * @param {string} attribute - 属性（開発者、自治体、清掃業者）
   * @returns {Promise<Array<string>>} 企業リスト
   */
  async getCompanyList(attribute) {
    const service = getApiService()
    return await service.getCompanyList(attribute)
  },

  /**
   * API設定を更新（主にテスト・開発用）
   * @param {Object} config - 新しい設定
   * @returns {Object} 更新された設定
   */
  updateConfig(config) {
    Object.assign(API_CONFIG, config)
    console.log('[API Service] 設定を更新しました:', API_CONFIG)
    return { ...API_CONFIG }
  },

  /**
   * 現在の設定を取得
   * @returns {Object} 現在の設定
   */
  getConfig() {
    return { ...API_CONFIG }
  }
}

export default developerSettingsApi
